import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FormattedMessage } from "react-intl";
import { Spin, Select } from 'antd';
import { Form } from "react-final-form";

import InputField from "components/InputField/InputField";
import InputPhoneField from "components/InputPhoneField/InputPhoneField";
import SelectField from "components/SelectField/SelectField";
import Button from "components/Button/Button";
import { getLocalizedKey } from "utils/utils";
import FileUploader from "components/FileUploader/FileUploader";
import { contributionsStore } from "@/store/contributionsStore";
import { pathBuilder, urlLocations } from "routes/urlLocations";
import GoBack from "containers/styled/StyledGoBack";
import StyledContainer from "components/styled/StyledContainer";
import { appInfoStore, userStore } from '@/store/newStores';
import FormInput from '@/components/InputField/FormInput';
import { MemberSelector } from '@/components/common/MemberSelector';
import { MemberItem } from '@/components/common/MemberItem';
import { basePressableStyles } from '@/components/common/buttonStyles';
import { Toggle } from '@/components/ui/toggle';
import { Pencil } from 'lucide-react';

const { Option } = Select;

const ContributionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const contributionId = id !== 'new' ? id : null;
  const [contribution, setContribution] = useState({});
  const [isEditable, setIsEditable] = useState(!contributionId); // Editable by default for new contributions
  const isLoading = contributionsStore.isLoading.use();

  const churches = Object.values(appInfoStore.churches.get());
  const fathers = Object.values(appInfoStore.fathers.get());
  const currentUserChurchId = userStore.user.get()?.church?.id;

  // Fetch contribution data when contributionId changes
  useEffect(() => {
    const fetchContribution = async () => {
      if (contributionId) {
        try {
          const fetchedContribution = await contributionsStore.getContributionById(contributionId);
          setContribution(fetchedContribution || {});
        } catch (error) {
          console.error('Error fetching contribution:', error);
          setContribution({});
        }
      } else {
        setContribution({});
      }
    };

    fetchContribution();
  }, [contributionId]);

  const initialValues = {
    ...(contributionId ? {
      ...contribution,
      father: contribution?.father?.id,
      church: contribution?.church?.id,
    } : {})
  };

  //TODO Change to if created by the same user
  const hasPermissionToEdit = !initialValues?.church || currentUserChurchId === initialValues?.church;
  const isFormDisabled = !isEditable || !hasPermissionToEdit;

  const handleSuccess = () => {
    navigate(urlLocations.contributions);
  };

  const toggleEditMode = () => {
    setIsEditable(!isEditable);
  };

  const onSubmit = async (values) => {
    try {
      if (values?.id) {
        await contributionsStore.updateContribution(values.id, values);
      } else {
        await contributionsStore.createContribution(values, contribution?.member?.id);
      }
      handleSuccess();
    } catch (error) {
      console.error('Error submitting contribution:', error);
    }
  };

  const onMemberChange = (member, form) => {
    console.log("Selected member:", member);
    // Auto-fill form fields with member information
    if (member) {
      const memberData = {
        fullName: member.firstName + ' ' + member.secondName + ' ' + member.lastName || '',
        mobileNumber: member.mobile || '',
        emiratesID: member.emiratesID || member.passportNumber || '',
        church: member.church?.id || '',
      };

      // Update form fields
      form.change('fullName', memberData.fullName);
      form.change('mobileNumber', memberData.mobileNumber);
      form.change('emiratesID', memberData.emiratesID);
      
      // Auto-fill church if member has one
      if (memberData.church) {
        form.change('church', memberData.church);
      }

      // Update contribution state to keep it in sync
      setContribution(prevContribution => ({
        ...prevContribution,
        ...memberData,
        member: member // Store the full member object for reference
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-5">
      <StyledContainer>
        <Spin spinning={!!isLoading}>
          <div className="bg-white rounded-lg p-8 shadow-lg max-w-3xl mx-auto">
            <div className="relative flex justify-between items-center mb-5">
              <GoBack className='' to={urlLocations.contributions} label="Contributions.BackButton"/>
              <div className="text-2xl font-semibold text-gray-800 text-center">
                <FormattedMessage id={contributionId ? "Contributions.EditTitle" : "Contributions.AddTitle"} />
              </div>
              {hasPermissionToEdit && contributionId ? (
                <Toggle
                  pressed={isEditable}
                  onPressedChange={toggleEditMode}
                  variant="outline"
                  className="data-[state=on]:bg-blue-500 data-[state=on]:text-white"
                >
                  <Pencil /> {isEditable ? <FormattedMessage id="Contributions.Editing" /> : <FormattedMessage id="Contributions.Edit" />}
                </Toggle>
              ) : <div className='w-10'/>}
            </div>
            <Form
              onSubmit={onSubmit}
              initialValues={initialValues}
              render={({ handleSubmit, pristine, submitting, form }) => (
                <>
                  {!contributionId ? 
                  <MemberSelector
                    className="mb-8"
                    placeholder="Select a member..."
                    onSelectedMemberChange={(member) => {
                      onMemberChange(member, form);
                    }}
                  /> : contribution?.member && 
                  <div className="mb-8 flex items-center justify-between">
                    <Link to={pathBuilder.memberDetails(contribution?.member)} className="flex-1">
                      <MemberItem member={contribution?.member} className={`p-2 ${basePressableStyles}`}/>
                    </Link>
                  </div>
                  }
                <form onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <InputField
                        disabled={isFormDisabled}
                        name={"fullName"}
                        placeholder={"Contributions.Form.FullName"}
                        label={"Contributions.Form.FullName"}
                      />
                    </div>
                    <div>
                      <InputPhoneField
                        disabled={isFormDisabled}
                        name={"mobileNumber"}
                        placeholder={"Contributions.Form.Mobile"}
                        label={"Contributions.Form.Mobile"}
                      />
                    </div>
                    <div>
                      <InputField
                        disabled={isFormDisabled}
                        name={"emiratesID"}
                        placeholder={"Contributions.Form.EmiratesID"}
                        label={"Contributions.Form.EmiratesID"}
                      />
                    </div>
                    <div>
                      <FormInput
                        disabled={isFormDisabled}
                        name={"amount"}
                        placeholder={"Contributions.Form.Amount"}
                        label={"Contributions.Form.Amount"}
                        inputType="currency"
                        currency="AED"
                      />
                    </div>
                    <div>
                      <SelectField
                        disabled={isFormDisabled}
                        name={"church"}
                        placeholder={"Contributions.Form.Church"}
                        label={"Contributions.Form.Church"}
                      >
                        {churches?.map((church) => (
                          <Option key={church?.id} value={church?.id}>
                            {church[getLocalizedKey("name")]}
                          </Option>
                        ))}
                      </SelectField>
                    </div>
                    <div>
                      <SelectField
                        disabled={isFormDisabled}
                        name={"father"}
                        placeholder={"Contributions.Form.Father"}
                        label={"Contributions.Form.Father"}
                      >
                        {fathers?.map((father) => (
                          <Option key={father?.id} value={father?.id}>
                            {father[getLocalizedKey("name")]}
                          </Option>
                        ))}
                      </SelectField>
                    </div>
                    <div className="col-span-full">
                      <FormInput 
                        name={"reason"} 
                        label={"Contributions.Form.Reason"} 
                        placeholder="Contributions.Form.Reason" 
                        isTextArea={true} 
                        disabled={isFormDisabled}
                      />
                    </div>
                    {/* <div className="col-span-full">
                      <FileUploader
                        formName={"contributions"}
                        name={"attachments"}
                        isFormDisabled={isFormDisabled}
                      />
                    </div> */}
                  </div>
                  {isEditable && hasPermissionToEdit && (
                    <div className="flex justify-center pt-6 border-t border-gray-200">
                      <Button
                        disabled={pristine || submitting}
                        type="button"
                        onClick={() => {
                          form.submit();
                        }}
                      >
                        <FormattedMessage id={ contributionId ? "Contributions.EditButton" : "Contributions.AddButton" } />
                      </Button>
                    </div>
                  )}
                </form>
                </>
              )}
            />
          </div>
        </Spin>
      </StyledContainer>
    </div>
  );
};

export default ContributionDetails; 